import { EmptyGuid, UserRightsEnum } from '../../../../utilities/Constants';
import {
    ICaseExpertModel,
    ICreateCaseExpertModel,
    IViewCaseExpertModelWithDefaultEdit,
} from '../../../../interfaces/ICaseExpert';

import { AddCaseExpertDialog } from './AddCaseExpertDialog';
import Authorization from '../../../../stores/Authorization';
import { CaseExpertTable } from './CaseExpertTable';
import { CheckmateDialog } from '../../../shared/dialog';
import { IExpertListModel } from '../../../../interfaces/IExpert';
import { IUserModel } from '../../../../interfaces/IUser';
import { Loader } from '../../../shared/Loader';
import { cloneDeep } from 'lodash';
import { showErrorToast } from '../../../../shared/ToastUtils';
import { useCaseExpertCreateMutation } from './useCaseExpertCreateMutation';
import { useCaseExpertDeleteMutation } from './useCaseExpertDeleteMutation';
import { useState } from 'react';
import { useZoneContext } from '../../../../contexts/useZoneContext';

export function CaseExpertsList({
    caseExperts,
    caseGuid,
    displayTableWhenEmpty,
    enableEasyUpdateColumn,
    getEasyUpdateIconColorStyle,
    hideDelete,
    onEasyUpdateClick,
    onSetCurrentCaseExpert,
    showCaseName,
    user,
    readonly,
    refreshParent,
    isQuadScope,
}: {
    caseExperts: ICaseExpertModel[];
    caseGuid?: string;
    displayTableWhenEmpty?: boolean;
    enableEasyUpdateColumn?: boolean;
    getEasyUpdateIconColorStyle?: (caseGuid: string, noteGuid?: string) => string;
    hideDelete?: boolean;
    onEasyUpdateClick?: (caseGuid: string) => void;
    onSetCurrentCaseExpert?: (caseExpert: ICaseExpertModel | null) => void;
    showCaseName?: boolean;
    user: IUserModel;
    readonly?: boolean;
    refreshParent?: (caseExperts: ICaseExpertModel[]) => void;
    isQuadScope? : boolean;
}) {
    const zoneContext = useZoneContext();
    const authorizedToCreate = Authorization.userHasRight(
        UserRightsEnum.AddCaseExpert,
        user,
        zoneContext.zoneGuid
    );
    const authorizedToDelete = Authorization.userHasRight(
        UserRightsEnum.DeleteCaseExpert,
        user,
        zoneContext.zoneGuid
    );
    const authorizedToEdit = Authorization.userHasRight(
        UserRightsEnum.UpdateCaseExpert,
        user,
        zoneContext.zoneGuid
    );
    const authorizedToView = Authorization.userHasRight(
        UserRightsEnum.ViewCaseExpert,
        user,
        zoneContext.zoneGuid
    );

    const createCaseExpertMutation = useCaseExpertCreateMutation();
    const deleteMutation = useCaseExpertDeleteMutation();

    const [caseExpertToRemove, setCaseExpertToRemove] = useState<ICaseExpertModel | null>(null);
    const [addCaseExpertCaseGuid, setAddCaseExpertCaseGuid] = useState<string | undefined>();

    const executeDelete = (caseExpertGuid: string) => {
        setCaseExpertToRemove(null);
        deleteMutation
            .mutateAsync(caseExpertGuid)
            .then(() => {
                const filtered = caseExperts?.filter((ce) => ce.guid !== caseExpertGuid);
                if (typeof refreshParent === 'function') refreshParent(filtered ?? []);
            })
            .catch(() => {
                const selectedCaseExpert = caseExperts?.find((ce) => ce.guid === caseExpertGuid);
                showErrorToast(
                    <span>
                        Failed to save case expert&nbsp;
                        <strong>{selectedCaseExpert?.name}</strong>
                        &nbsp;for Case - {selectedCaseExpert?.caseName}
                    </span>
                );
            });
    };

    const handleAddClick = (caseGuid: string) => {
        setAddCaseExpertCaseGuid(caseGuid);
    };

    const handleCaseExpertRemoveClick = (caseExpertGuid: string) => {
        setCaseExpertToRemove(caseExperts?.find((ce) => ce.guid === caseExpertGuid) ?? null);
    };

    const handleCaseExpertViewEditClick = (caseExpertGuid: string, defaultIsEditMode: boolean) => {
        const found = caseExperts?.find((ce) => ce.guid === caseExpertGuid);

        const foundCopy = { ...found } as IViewCaseExpertModelWithDefaultEdit;

        foundCopy.defaultIsEditMode = defaultIsEditMode;

        if (typeof onSetCurrentCaseExpert === 'function') {
            onSetCurrentCaseExpert(foundCopy ?? null);
        }
    };

    const handleAddCaseExpertDialogCancel = () => {
        setAddCaseExpertCaseGuid(undefined);
    };

    const handleAddCaseExpertDialogSubmit = async (
        caseExpert: ICreateCaseExpertModel,
        selectedExpert: IExpertListModel | undefined
    ) => {
        const caseGuid = addCaseExpertCaseGuid;

        await createCaseExpertMutation
            .mutateAsync(caseExpert)
            .then(({ payload: createdCaseExpert }) => {
                if (createdCaseExpert) {
                    const caseExpertsCopy = cloneDeep(caseExperts);

                    const found = caseExpertsCopy.find(
                        (existing) =>
                            existing.expertGuid === EmptyGuid &&
                            createdCaseExpert.caseGuid === existing.caseGuid
                    );

                    if (found) {
                        found.name = `${createdCaseExpert.expert.lastName}, ${createdCaseExpert.expert.firstName}`;
                        found.expertGuid = createdCaseExpert.expertGuid;
                        found.guid = createdCaseExpert.guid;
                        found.disciplines = createdCaseExpert.expert.disciplines ?? [];
                        found.leadDefendant = createdCaseExpert.leadDefendant ?? '';
                        found.coDefendants = createdCaseExpert.coDefendants ?? '';
                        found.expertReportStatusTypeId =
                            createdCaseExpert.expertReportStatusTypeId ?? 0;
                        found.expertRetainedByTypeId =
                            createdCaseExpert.expertRetainedByTypeId ?? 0;
                        found.purposeOfRetention = createdCaseExpert.purposeOfRetention;
                    } else {
                        const newCaseExpert: ICaseExpertModel = {
                            caseGuid: '',
                            caseName: '',
                            name: `${createdCaseExpert.expert.lastName}, ${createdCaseExpert.expert.firstName}`,
                            expertGuid: createdCaseExpert.expertGuid,
                            guid: createdCaseExpert.guid,
                            disciplines: createdCaseExpert.expert.disciplines ?? [],
                            leadDefendant: createdCaseExpert.leadDefendant ?? '',
                            coDefendants: createdCaseExpert.coDefendants ?? '',
                            expertReportStatusTypeId:
                                createdCaseExpert.expertReportStatusTypeId ?? 0,
                            expertRetainedByTypeId: createdCaseExpert.expertRetainedByTypeId ?? 0,
                            purposeOfRetention: createdCaseExpert.purposeOfRetention,
                        };
                        caseExpertsCopy.push(newCaseExpert);
                    }

                    if (typeof refreshParent === 'function') {
                        refreshParent(caseExpertsCopy);
                    }

                    setAddCaseExpertCaseGuid(undefined);
                }
            })
            .catch(() => {
                const caseName = caseExperts.find((ce) => ce.caseGuid === caseGuid)?.caseName;

                showErrorToast(
                    <span>
                        Failed to add case expert&nbsp;
                        <strong>
                            {selectedExpert?.firstName} {selectedExpert?.lastName}
                        </strong>
                        {caseName && <>&nbsp;for Case - {caseName}</>}
                    </span>
                );
            });
    };

    return (
        <div className="container-fluid">
            {caseGuid && authorizedToCreate && !readonly && (
                <SectionHeader onAddClick={() => handleAddClick(caseGuid)} />
            )}
            <CaseExpertTable
                canDelete={authorizedToDelete && !hideDelete && !readonly}
                canEdit={authorizedToEdit && !readonly}
                canView={authorizedToView}
                canAddWhenEmpty={authorizedToCreate}
                caseExperts={caseExperts ?? []}
                getEasyUpdateIconColorStyle={getEasyUpdateIconColorStyle}
                hideNoRecordsMessage={displayTableWhenEmpty}
                noRecordsMessage="No case experts have been added to this case."
                onCaseExpertRemoveClick={handleCaseExpertRemoveClick}
                onCaseExpertViewEditClick={handleCaseExpertViewEditClick}
                onEasyUpdateClick={onEasyUpdateClick}
                onRowAddClick={handleAddClick}
                showCaseName={showCaseName}
                showEasyUpdateIcon={enableEasyUpdateColumn}
                user={user}
                isQuadScope={isQuadScope}
            />
            {caseExpertToRemove && (
                <CheckmateDialog
                    body={`Are you sure you wish to remove expert ${caseExpertToRemove?.name} from this case?`}
                    cancelText="Cancel"
                    closeButtonClassName="btn btn-default float-end"
                    confirmButtonClassName="btn btn-black float-end "
                    confirmText="Remove"
                    dialogClassName="confirm-document-delete-dialog"
                    handleClose={() => setCaseExpertToRemove(null)}
                    handleConfirm={() => executeDelete(caseExpertToRemove?.guid ?? '')}
                    isShowingModal
                    title="Remove"
                />
            )}

            {addCaseExpertCaseGuid && (
                <AddCaseExpertDialog
                    caseGuid={addCaseExpertCaseGuid}
                    title="Add Case Expert"
                    open={true}
                    onCancelClick={handleAddCaseExpertDialogCancel}
                    onSubmitClick={handleAddCaseExpertDialogSubmit}
                    isSaving={createCaseExpertMutation.isLoading}
                />
            )}
        </div>
    );
}

function SectionHeader({ onAddClick }: { onAddClick: () => void }) {
    return (
        <div className="row mb-1">
            <button className="btn-no-bg float-end col-sm-12" onClick={onAddClick}>
                <span className="btn-green btn float-end btn-icon">
                    <i className="fal fa-lg fa-plus color-white" />
                </span>
            </button>
        </div>
    );
}
